'use client';

import { useSearchParams } from 'next/navigation';

import { TAssessmentType } from '@northladder/apis';
import { useRouter } from '@northladder/i18n';

type TNavFunc = 'push' | 'replace';

/**
 * Returns a function that can be used to generate the route url.
 * This will inject the `sessionId` and `assessmentType` from the URL query params.
 *
 * @param baseUrl - The base path to which the route should navigate to.
 */
export const useRouteURL = (baseUrl?: string) => {
  const { replace, push } = useRouter();
  const searchParams = useSearchParams();

  const sessionId = (searchParams?.get('sessionId') || '') as string;
  const platform = (searchParams?.get('platform') || '') as string;
  const assessmentType = (searchParams?.get('assessmentType') ||
    'APPLE') as TAssessmentType;

  /**
   * Gets the full URL to which the route should navigate to.
   *
   * @param basePath - The base path to which the route should navigate to.
   */
  const getRouteURL = (basePath?: string) => {
    const path = basePath || baseUrl || '';
    return `${path}?platform=${platform}&sessionId=${sessionId}&assessmentType=${assessmentType}`;
  };

  /**
   * Navigates the page to a provided route.
   *
   * @param basePath - The base path to which the route should navigate to.
   * @param type - Whether to `replace` the current route or `push` a new one.
   */
  const navigateToRoute = (basePath: string, type?: TNavFunc) => {
    const navFunc = type === 'push' ? push : replace;
    navFunc(getRouteURL(basePath));
  };

  /**
   * Goes back to the root path.
   * @param type - Whether to `replace` the current route or `push` a new one.
   */
  const resetNavPath = (type?: TNavFunc) => {
    const navFunc = type === 'push' ? push : replace;
    navFunc('/');
  };

  return {
    assessmentType,
    getRouteURL,
    navigateToRoute,
    platform,
    resetNavPath,
    sessionId,
  };
};
